import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getMinimalEvents } from "apis/EventServiceApi";
import { IEventListData, SearchDocumentsPayload, TEventTab } from "store/eventList/EventList.types";
import { RootState } from "store/types";

interface ProfileEventsState {
  events: IEventListData[];
  totalPages: number;
  currentPage: number;
  isLoading: boolean;
  error: string | null;
}

const initialState: ProfileEventsState = {
  events: [],
  totalPages: 1,
  currentPage: 1,
  isLoading: false,
  error: null,
};

export const fetchProfileEvents = createAsyncThunk(
  "profileEvents/fetchProfileEvents",
  async (page: number, { rejectWithValue }) => {
    try {
      const payload: SearchDocumentsPayload = {
        orderBy: "created_at",
        arrangeOrder: "asc",
        typeEvents: TEventTab.UPCOMING,
        dataPage: page,
        limit: 10,
      };
      const response = await getMinimalEvents(payload);
      return {
        data: response.data,
        totalPages: response.pagination?.total_pages || 1,
        currentPage: page,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "Error fetching events");
    }
  }
);

const profileEventsSlice = createSlice({
  name: "profileEvents",
  initialState,
  reducers: {
    resetProfileEvents: (state) => {
      state.events = [];
      state.totalPages = 1;
      state.currentPage = 1;
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfileEvents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProfileEvents.fulfilled, (state, action) => {
        if (action.payload.currentPage === 1) {
          state.events = action?.payload?.data ?? [];
        } else {
          state.events = [...state.events, ...action.payload.data];
        }
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
        state.isLoading = false;
      })
      .addCase(fetchProfileEvents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetProfileEvents } = profileEventsSlice.actions;
export default profileEventsSlice.reducer;

export const selectProfileEvents = (state: RootState) => state.profileEvents;
